import { useState, useEffect } from "react";
import { isBlankHtml } from "utils/htmlContent";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import Rating from "@mui/material/Rating";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Chip from "@mui/material/Chip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDAlert from "components/MDAlert";
import RichTextEditor from "components/RichTextEditor";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { adminApi } from "services/api";

// Below md the DataTable shows each row as a card, which would print long text in full; clamp it
// there (column `clampLines`, default 3) so cards stay scannable. Desktop cells are unchanged.
function ClampedCell({ value, column }) {
  return (
    <MDBox
      component="span"
      color="inherit"
      sx={({ breakpoints }) => ({
        [breakpoints.down("md")]: {
          display: "-webkit-box",
          WebkitLineClamp: column.clampLines || 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        },
      })}
    >
      {value}
    </MDBox>
  );
}

ClampedCell.propTypes = {
  value: PropTypes.node,
  column: PropTypes.shape({ clampLines: PropTypes.number }).isRequired,
};

// Full-screen dialog (below sm): let the form content scroll between a pinned title and action bar.
const fullScreenForm = { display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 };

function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  // Field names match the Testimonial model (name/position/featured). The page used to send
  // author/role, which the server ignored or rejected ("Name and content are required").
  const [formData, setFormData] = useState({
    name: "",
    position: "",
    company: "",
    content: "",
    rating: 5,
    image: "",
    // New testimonials go straight onto the website; untick to keep one as a draft.
    featured: true,
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getTestimonials();
      setTestimonials(data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch testimonials");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setEditMode(false);
    setFormData({
      name: "",
      position: "",
      company: "",
      content: "",
      rating: 5,
      image: "",
      // New testimonials go straight onto the website; untick to keep one as a draft.
      featured: true,
    });
    setOpenDialog(true);
  };

  const handleEdit = (testimonial) => {
    setEditMode(true);
    setCurrentId(testimonial._id);
    setFormData({
      name: testimonial.name || "",
      position: testimonial.position || "",
      company: testimonial.company || "",
      content: testimonial.content,
      rating: testimonial.rating || 5,
      image: testimonial.image || "",
      featured: !!testimonial.featured,
    });
    setOpenDialog(true);
    handleMenuClose();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setCurrentId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleRatingChange = (event, newValue) => {
    // Clicking the current star reports null; keep the rating instead of clearing it.
    if (!newValue) return;
    setFormData({
      ...formData,
      rating: newValue,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (isBlankHtml(formData.content)) {
      setError("Testimonial content is required");
      return;
    }

    try {
      if (editMode) {
        await adminApi.updateTestimonial(currentId, formData);
        setSuccess("Testimonial updated successfully");
      } else {
        await adminApi.createTestimonial(formData);
        setSuccess("Testimonial created successfully");
      }
      handleCloseDialog();
      fetchTestimonials();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "Operation failed");
    }
  };

  const handleDelete = async () => {
    if (!selectedTestimonial) return;

    try {
      await adminApi.deleteTestimonial(selectedTestimonial._id);
      setSuccess("Testimonial deleted successfully");
      fetchTestimonials();
      handleMenuClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete testimonial");
    }
  };

  const handleMenuOpen = (event, testimonial) => {
    setAnchorEl(event.currentTarget);
    setSelectedTestimonial(testimonial);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTestimonial(null);
  };

  const columns = [
    {
      Header: "name",
      accessor: "name",
      width: "18%",
      align: "left",
      Cell: ClampedCell,
      clampLines: 2,
    },
    {
      Header: "position",
      accessor: "position",
      width: "18%",
      align: "left",
      Cell: ClampedCell,
      clampLines: 2,
    },
    { Header: "content", accessor: "content", width: "34%", align: "left" },
    { Header: "rating", accessor: "rating", width: "10%", align: "center" },
    { Header: "website", accessor: "website", width: "10%", align: "center" },
    { Header: "actions", accessor: "actions", width: "10%", align: "center" },
  ];

  const rows = testimonials.map((testimonial) => {
    // Content is rich-text HTML; show a plain-text preview rather than raw tags.
    const plain = (testimonial.content || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return {
      name: testimonial.name || "—",
      position: [testimonial.position, testimonial.company].filter(Boolean).join(", ") || "N/A",
      content: plain.substring(0, 60) + (plain.length > 60 ? "..." : ""),
      website: (
        <Chip
          size="small"
          label={testimonial.featured ? "Shown" : "Hidden"}
          color={testimonial.featured ? "success" : "default"}
          variant={testimonial.featured ? "filled" : "outlined"}
        />
      ),
      rating: <Rating value={testimonial.rating || 5} readOnly size="small" />,
      actions: (
        <IconButton onClick={(e) => handleMenuOpen(e, testimonial)}>
          <Icon>more_vert</Icon>
        </IconButton>
      ),
    };
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexWrap="wrap"
                gap={1.5}
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Testimonials
                </MDTypography>
                <MDButton variant="contained" color="white" onClick={handleOpenDialog}>
                  Add Testimonial
                </MDButton>
              </MDBox>

              {error && (
                <MDBox pt={2} px={2}>
                  <MDAlert color="error" dismissible onClose={() => setError("")}>
                    {error}
                  </MDAlert>
                </MDBox>
              )}

              {success && (
                <MDBox pt={2} px={2}>
                  <MDAlert color="success" dismissible onClose={() => setSuccess("")}>
                    {success}
                  </MDAlert>
                </MDBox>
              )}

              <MDBox pt={3}>
                {loading ? (
                  <MDBox p={3} textAlign="center">
                    <MDTypography variant="body2">Loading...</MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>{editMode ? "Edit Testimonial" : "Add New Testimonial"}</DialogTitle>
        <form onSubmit={handleSubmit} style={fullScreenDialog ? fullScreenForm : undefined}>
          <DialogContent>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                required
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Position"
                name="position"
                value={formData.position}
                onChange={handleChange}
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <RichTextEditor
                label="Content *"
                value={formData.content}
                onChange={(value) => setFormData({ ...formData, content: value })}
                error={!formData.content && error}
                helperText="Write the testimonial content with formatting"
                height={250}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Image URL"
                name="image"
                value={formData.image}
                onChange={handleChange}
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <MDTypography variant="caption" fontWeight="bold" mb={1}>
                Rating
              </MDTypography>
              <MDBox mt={1}>
                <Rating
                  name="rating"
                  value={formData.rating}
                  onChange={handleRatingChange}
                  size="large"
                />
              </MDBox>
            </MDBox>
            <FormControlLabel
              control={
                <Switch
                  checked={!!formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                />
              }
              label="Show on the website"
            />
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCloseDialog} color="secondary">
              Cancel
            </MDButton>
            <MDButton type="submit" color="info">
              {editMode ? "Update" : "Create"}
            </MDButton>
          </DialogActions>
        </form>
      </Dialog>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleEdit(selectedTestimonial)}>Edit</MenuItem>
        <MenuItem onClick={handleDelete}>Delete</MenuItem>
      </Menu>

      <Footer />
    </DashboardLayout>
  );
}

export default Testimonials;
